import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const etfData = [
  {
    ticker: 'VIG',
    name: 'Vanguard Dividend Appreciation ETF',
    expenseRatio: 0.06,
    aum: 81.3,
    ytdReturn: 11.31
  },
  {
    ticker: 'SCHD',
    name: 'Schwab U.S. Dividend Equity ETF',
    expenseRatio: 0.06,
    aum: 58.1,
    ytdReturn: 9.38
  },
  {
    ticker: 'VYM',
    name: 'Vanguard High Dividend Yield ETF',
    expenseRatio: 0.06,
    aum: 55.9,
    ytdReturn: 11.44
  },
  {
    ticker: 'DGRO',
    name: 'iShares Core Dividend Growth ETF',
    expenseRatio: 0.08,
    aum: 28.8,
    ytdReturn: 12.36
  },
  {
    ticker: 'SDY',
    name: 'SPDR S&P Dividend ETF',
    expenseRatio: 0.35,
    aum: 20.7,
    ytdReturn: 9.17
  },
  {
    ticker: 'DVY',
    name: 'iShares Select Dividend ETF',
    expenseRatio: 0.38,
    aum: 19.2,
    ytdReturn: 12.21
  },
  {
    ticker: 'DGRW',
    name: 'WisdomTree U.S. Quality Dividend Growth Fund',
    expenseRatio: 0.28,
    aum: 13.6,
    ytdReturn: 13.11
  },
  {
    ticker: 'NOBL',
    name: 'ProShares S&P 500 Dividend Aristocrats ETF',
    expenseRatio: 0.35,
    aum: 12.1,
    ytdReturn: 7.30
  },
  {
    ticker: 'RDVY',
    name: 'First Trust Rising Dividend Achievers ETF',
    expenseRatio: 0.49,
    aum: 11.3,
    ytdReturn: 10.92
  },
  {
    ticker: 'HDV',
    name: 'iShares Core High Dividend ETF',
    expenseRatio: 0.08,
    aum: 10.8,
    ytdReturn: 14.15
  }
];

function ETFList() {
  const [orderBy, setOrderBy] = useState('aum');
  const [order, setOrder] = useState('desc');

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = React.useMemo(() => {
    const comparator = (a, b) => {
      if (order === 'desc') {
        return b[orderBy] - a[orderBy];
      }
      return a[orderBy] - b[orderBy];
    };

    return [...etfData].sort(comparator);
  }, [order, orderBy]);

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Top Dividend ETFs
      </Typography>
      
      <TableContainer component={Paper} elevation={2} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'ticker'}
                  direction={orderBy === 'ticker' ? order : 'asc'}
                  onClick={() => handleSort('ticker')}
                >
                  Ticker
                </TableSortLabel>
              </TableCell>
              <TableCell>Fund Name</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'expenseRatio'}
                  direction={orderBy === 'expenseRatio' ? order : 'asc'}
                  onClick={() => handleSort('expenseRatio')}
                >
                  Expense Ratio
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  AUM
                  <Tooltip title="Assets Under Management (Billions)">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <TableSortLabel
                    active={orderBy === 'aum'}
                    direction={orderBy === 'aum' ? order : 'asc'}
                    onClick={() => handleSort('aum')}
                  />
                </Box>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'ytdReturn'}
                  direction={orderBy === 'ytdReturn' ? order : 'asc'}
                  onClick={() => handleSort('ytdReturn')}
                >
                  YTD Return
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((etf) => (
              <TableRow
                key={etf.ticker}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  <Typography variant="body2" fontWeight="bold">
                    {etf.ticker}
                  </Typography>
                </TableCell>
                <TableCell>{etf.name}</TableCell>
                <TableCell align="right">{etf.expenseRatio.toFixed(2)}%</TableCell>
                <TableCell align="right">${etf.aum.toFixed(1)}B</TableCell>
                <TableCell align="right">{etf.ytdReturn.toFixed(2)}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        * Data as of December 2024. Past performance does not guarantee future results.
      </Typography>
    </Container>
  );
}

export default ETFList;